import "swiper/css/effect-fade";

import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import PlayFocusActiveIcon from "src/assets/all-icons/play-fullscreen-active-icon.jsx";
import PlayFocusInactiveIcon from "src/assets/all-icons/play-fullscreen-inactive-icon.jsx";
import PlayIcon from "src/assets/all-icons/play-icon.jsx";
import PlayNextIcon from "src/assets/all-icons/play-next-icon.jsx";
import PlayPauseIcon from "src/assets/all-icons/play-pause-icon.jsx";
import PlayPreviousIcon from "src/assets/all-icons/play-previous-icon.jsx";
import PlayShuffleActiveIcon from "src/assets/all-icons/play-shuffle-active-icon.jsx";
import PlayShuffleInactiveIcon from "src/assets/all-icons/play-shuffle-inactive-icon.jsx";
import { Autoplay, EffectFade } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import XIcon from '@/shared-components/icons/components/public/XIcon';

import PlayArtlistSlide from './components/PlayArtlistSlide';

import './index.css';

export default function PlayArtlist() {
  const [artlistPlaying, setArtlistPlaying] = useState(true);
  const [isShuffleActive, setIsShuffleActive] = useState(false);
  const [swiperKey, setSwiperKey] = useState(0);
  const [showControls, setShowControls] = useState(false);
  const [isFocusActive, setIsFocusActive] = useState(false);
  const [swiper, setSwiper] = useState(null);
  const isTouchStartTriggered = useRef(false);
  const containerElement = useRef();
  const location = useLocation();
  const navigate = useNavigate();
  const timeoutId = useRef();
  const artlistStateArtworks = location.state?.artlistArtworks;
  const hasArtworks = Array.isArray(artlistStateArtworks) && artlistStateArtworks.length > 0;
  const originalArtworks = useRef(artlistStateArtworks ?? []);
  const [artlistArtworks, setArtlistArtworks] = useState(originalArtworks.current);

  useEffect(() => {
    if(containerElement.current && containerElement.current.requestFullscreen) {
      containerElement.current.requestFullscreen();
    }
  }, []);

  function pauseArtlist() {
    setArtlistPlaying(false);
    if(swiper) {
      swiper.autoplay.stop();
    }
  }

  function resumeArtlist() {
    setArtlistPlaying(true);
    if(swiper) {
      swiper.autoplay.start();
    }
  }

  function handleShowControls() {
    setShowControls(true);
    clearTimeout(timeoutId.current);
    timeoutId.current = setTimeout(()=>setShowControls(false), 2000);
  }

  function handleTouchEnd(e) {
    isTouchStartTriggered.current = true;

    if(showControls && (e.target.classList.contains("play-artlist__controls") || e.target.classList.contains("play-artlist__controls-wrap"))) {
      clearTimeout(timeoutId.current);
      setShowControls(false);
    } else {
      handleShowControls();
    }
  }

  function handleMouseMove() {
    if(isTouchStartTriggered.current) {
      isTouchStartTriggered.current = false;
    } else {
      handleShowControls();
    }
  }

  function shuffleArtworks(array) {
    const shuffled = [...array];
    for(let i = shuffled.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    return shuffled;
  }

  function resetSwiperWithArtworks(nextArtworks) {
    setArtlistArtworks(nextArtworks);
    setSwiper(null);
    setSwiperKey((prevKey)=>prevKey + 1);
  }

  function handleToggleShuffle() {
    if(!artlistArtworks.length) {
      return;
    }

    const currentIndex = swiper?.realIndex ?? 0;
    const safeIndex = currentIndex >= 0 && currentIndex < artlistArtworks.length ? currentIndex : 0;
    const currentArtwork = artlistArtworks[safeIndex] ?? artlistArtworks[0];

    if(!currentArtwork) {
      return;
    }
    const nextShuffleState = !isShuffleActive;
    setIsShuffleActive(nextShuffleState);

    const originalList = originalArtworks.current ?? [];
    let nextArtworks = [];

    if(nextShuffleState) {
      const remainingArtworks = shuffleArtworks(
        originalList.filter((artwork)=>artwork._id !== currentArtwork._id)
      );
      nextArtworks = [currentArtwork, ...remainingArtworks];
    } else {
      const sequentialIndex = originalList.findIndex((artwork)=>artwork._id === currentArtwork._id);

      if(sequentialIndex === -1) {
        const dedupedOriginal = originalList.filter((artwork)=>artwork._id !== currentArtwork._id);
        nextArtworks = [currentArtwork, ...dedupedOriginal];
      } else {
        nextArtworks = [
          ...originalList.slice(sequentialIndex),
          ...originalList.slice(0, sequentialIndex)
        ];
      }
    }

    resetSwiperWithArtworks(nextArtworks);
  }

  function goToPreviousArtwork() {
    if(!swiper) {
      return;
    }

    swiper.slidePrev(700);
  }

  function goToNextArtwork() {
    if(!swiper) {
      return;
    }

    swiper.slideNext(700);
  }

  function handleToggleFocus() {
    setIsFocusActive((prevState) => !prevState);
  }

  useEffect(() => {
    if(!swiper) {
      return;
    }

    if(artlistPlaying) {
      swiper.autoplay.start();
    } else {
      swiper.autoplay.stop();
    }
  }, [swiper, artlistPlaying]);

  useEffect(() => {
    if(Array.isArray(artlistStateArtworks) && originalArtworks.current !== artlistStateArtworks) {
      originalArtworks.current = artlistStateArtworks;
      resetSwiperWithArtworks(artlistStateArtworks);
      setIsShuffleActive(false);
    }
  }, [artlistStateArtworks]);

  if(!hasArtworks) {
    return <Navigate to="/" replace/>;
  }

  return (
    <>
      <div
        className={`play-artlist__container${showControls ? " controls-active" : ""}${isFocusActive ? " focus-active" : ""}`}
        onMouseMove={handleMouseMove}
        onTouchEnd={handleTouchEnd}
        ref={containerElement}
      >
        <div className="play-artlist__exit-circle">
          <XIcon className="play-artlist__exit-icon" onClick={()=>navigate(-1)}/>
        </div>
        <div className="play-artlist__controls">
          <div className="play-artlist__controls-wrap">
            <div className="play-artlist__controls-group">
              {
                isShuffleActive ?
                  <PlayShuffleActiveIcon
                    className="play-artlist__controls-icon play-artlist__controls-icon--shuffle-active"
                    onClick={handleToggleShuffle}
                  />
                  :
                  <PlayShuffleInactiveIcon
                    className="play-artlist__controls-icon"
                    onClick={handleToggleShuffle}
                  />
              }
              <PlayPreviousIcon
                className="play-artlist__controls-icon"
                onClick={goToPreviousArtwork}
              />
              {
                artlistPlaying ?
                  <PlayPauseIcon
                    className="play-artlist__controls-icon"
                    onClick={pauseArtlist}
                  />
                  :
                  <PlayIcon
                    className="play-artlist__controls-icon"
                    onClick={resumeArtlist}
                  />
              }
              <PlayNextIcon
                className="play-artlist__controls-icon"
                onClick={goToNextArtwork}
              />
              {
                isFocusActive ?
                  <PlayFocusActiveIcon
                    className="play-artlist__controls-icon play-artlist__controls-icon--focus-active"
                    onClick={handleToggleFocus}
                  />
                  :
                  <PlayFocusInactiveIcon
                    className="play-artlist__controls-icon"
                    onClick={handleToggleFocus}
                  />
              }
            </div>
          </div>
        </div>

        <Swiper
          key={swiperKey}
          autoplay={{
            delay: Math.max(0, (location.state.slideDuration-2000)),
            disableOnInteraction: false
          }}
          speed={2000}
          effect="fade"
          fadeEffect={{ crossFade: true }}
          allowTouchMove={false}
          loop={true}
          className="play-artlist-swiper"
          modules={[EffectFade, Autoplay]}
          onInit={(swiperInstance)=>setSwiper(swiperInstance)}
        >
          {artlistArtworks.map((artwork) => (
            <SwiperSlide key={artwork._id}>
              <PlayArtlistSlide artwork={artwork} isFocused={isFocusActive} />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </>
  );
}
